import {
  CheckCircle2,
  CircleHelp,
  CircleSlash,
  Clock,
  type LucideIcon,
  TriangleAlert,
} from "lucide-react";
import { useSearchParams } from "react-router";

import { Card, CardContent } from "@/components/ui/card";
import { logger } from "@/lib/logger";
import { cn } from "@/lib/utils";

type ConsentResult =
  | "granted"
  | "reported"
  | "declined"
  | "expired"
  | "permissions_changed"
  | "invalid";

interface ResultCopy {
  icon: LucideIcon;
  iconClassName: string;
  title: string;
  message: string;
}

/**
 * The six fixed outcomes the backend's consent callback redirects with.
 *
 * Fixed on purpose: the query string is attacker-controllable (anyone can
 * craft `/sign-in-approval?result=…`), so the page maps it to one of these and
 * never renders the raw value.
 */
const RESULT_COPY: Record<ConsentResult, ResultCopy> = {
  granted: {
    icon: CheckCircle2,
    iconClassName: "text-green-400",
    title: "Approved",
    message: "Approved. The project's users can now sign in — you can close this tab.",
  },
  // Entra said consent was granted, but Scaffold UI could not confirm it from
  // the grant (unreadable, or still not shown after a few seconds): recorded
  // for IT to verify, never shown as approved.
  reported: {
    icon: CircleHelp,
    iconClassName: "text-amber-400",
    title: "Reported — not verified",
    message:
      "Entra reported the consent, but Scaffold UI couldn't confirm it. It stays in the IT approvals queue until it is checked or marked approved — you can close this tab.",
  },
  declined: {
    icon: CircleSlash,
    iconClassName: "text-[#9CA3A5]",
    title: "Not approved",
    message: "Consent was not granted. Nothing changed.",
  },
  expired: {
    icon: Clock,
    iconClassName: "text-amber-400",
    title: "Link expired",
    message:
      "This approval link has expired. If you did grant consent, a Scaffold UI admin can mark it approved; otherwise ask for a fresh link.",
  },
  // The link was minted for an older permission set: "Change permissions" has
  // changed it since, and an old link never records consent to the new set.
  permissions_changed: {
    icon: Clock,
    iconClassName: "text-amber-400",
    title: "Permissions changed since this link was sent",
    message:
      "The app's permissions changed after this approval link was sent, so this approval was not recorded. Get a fresh link from IT approvals in Scaffold UI, or ask whoever sent you this one for a new one.",
  },
  invalid: {
    icon: TriangleAlert,
    iconClassName: "text-[#EF4444]",
    title: "Link not recognised",
    message: "This approval link could not be verified. Nothing was recorded.",
  },
};

const KNOWN_RESULTS: readonly ConsentResult[] = [
  "granted",
  "reported",
  "declined",
  "expired",
  "permissions_changed",
];

function toConsentResult(value: string | null): ConsentResult {
  return KNOWN_RESULTS.find((known) => known === value) ?? "invalid";
}

/**
 * Where Entra's admin-consent prompt ends up, via the backend's callback.
 *
 * Public — outside both `ProtectedRoute` and `GuestRoute` — because the IT
 * admin who consents may not be a Scaffold UI user at all. It makes no API
 * calls: the backend has already recorded the outcome before redirecting here.
 */
export function SignInApprovalResultPage() {
  const [searchParams] = useSearchParams();
  const result = toConsentResult(searchParams.get("result"));
  const copy = RESULT_COPY[result];
  logger.debug("[sign-in-approval] result page:", result);

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#0A0E14] px-4">
      <Card className="w-full max-w-md overflow-hidden border-[#115cd3]/30 bg-[#1A1A2E]">
        <div className="flex justify-center bg-[#115cd3] py-8">
          <img src="/brand/norican-logo-white.svg" alt="Norican" className="h-10 w-auto" />
        </div>
        <CardContent className="space-y-4 pt-8 pb-8 text-center">
          <copy.icon className={cn("mx-auto size-10", copy.iconClassName)} aria-hidden />
          <h1 className="text-lg font-medium text-white">{copy.title}</h1>
          <p className="text-sm text-[#9CA3A5]">{copy.message}</p>
        </CardContent>
      </Card>
    </div>
  );
}
